// PostHog utilities - uses window.posthog created by gatsby-plugin-posthog

import { withPostHogEnvironmentProperties } from './posthogEnvironment';

interface PostHogInstance {
    __loaded?: boolean;
    capture: (eventName: string, properties?: Record<string, unknown>) => void;
    identify: (userId: string, properties?: Record<string, unknown>) => void;
    people: {
        set: (properties: Record<string, unknown>) => void;
    };
    startSessionRecording: () => void;
    stopSessionRecording: () => void;
    isFeatureEnabled: (flagKey: string) => boolean | undefined;
    getFeatureFlag: (flagKey: string) => string | boolean | undefined;
    onFeatureFlags?: (callback: () => void) => void;
    captureException?: (error: Error, properties?: Record<string, unknown>) => void;
}

declare global {
    interface Window {
        posthog?: PostHogInstance;
    }
}

const getPostHog = (): PostHogInstance | undefined => {
    if (typeof window === 'undefined') return undefined;
    return window.posthog;
};

const ensurePostHogInitialized = async (): Promise<boolean> => {
    const ph = getPostHog();
    if (!ph || !ph.__loaded) return false;
    return true;
};

const withPosthog = <T extends unknown[]>(fn: (ph: PostHogInstance, ...args: T) => void) => {
    return async (...args: T): Promise<void> => {
        const ph = getPostHog();
        if (!ph) return;
        if (!(await ensurePostHogInitialized())) return;
        fn(ph, ...args);
    };
};

export const usePostHogFeatureFlag = async (flagKey: string): Promise<boolean | undefined> => {
    const ph = getPostHog();
    if (!ph) return undefined;
    if (!(await ensurePostHogInitialized())) return undefined;
    return ph.isFeatureEnabled(flagKey);
};

export const getPostHogFeatureFlag = async (flagKey: string, defaultValue = false): Promise<boolean> => {
    const ph = getPostHog();
    if (!ph) return defaultValue;
    if (!(await ensurePostHogInitialized())) return defaultValue;
    const flagValue = ph.isFeatureEnabled(flagKey);
    return flagValue !== undefined ? flagValue : defaultValue;
};

export const capturePostHogEvent = withPosthog((ph, eventName: string, properties?: Record<string, unknown>) => {
    ph.capture(eventName, withPostHogEnvironmentProperties(properties));
});

export const identifyPostHogUser = withPosthog((ph, userId: string, properties?: Record<string, unknown>) => {
    ph.identify(userId, properties);
});

export const setPostHogPersonProperties = withPosthog((ph, properties: Record<string, unknown>) => {
    ph.people.set(properties);
});

export const startPostHogSession = withPosthog((ph) => {
    ph.startSessionRecording();
});

export const stopPostHogSession = withPosthog((ph) => {
    ph.stopSessionRecording();
});

// A/B Testing utilities
export const getPostHogVariant = async (experimentKey: string): Promise<string | undefined> => {
    const ph = getPostHog();
    if (!ph) return undefined;
    if (!(await ensurePostHogInitialized())) return undefined;
    const flag = ph.getFeatureFlag(experimentKey);
    return typeof flag === 'string' ? flag : undefined;
};

export const isPostHogVariant = async (experimentKey: string, variantName: string): Promise<boolean> => {
    const variant = await getPostHogVariant(experimentKey);
    return variant === variantName;
};

export const capturePostHogException = withPosthog((ph, error: Error, properties?: Record<string, unknown>) => {
    if (typeof ph.captureException === 'function') {
        // Enrich with additional error context
        const errorWithCause = error as Error & { cause?: unknown };
        const enrichedProperties = {
            ...properties,
            ...(error.name && { error_name: error.name }),
            ...(error.stack && { error_stack: error.stack }),
            ...(errorWithCause.cause ? { error_cause: errorWithCause.cause } : {})
        };

        ph.captureException(error, withPostHogEnvironmentProperties(enrichedProperties));
    }
});

export const capturePostHogMessage = withPosthog((ph, message: string, level: 'error' | 'warning' | 'info' = 'error', properties?: Record<string, unknown>) => {
    ph.capture(
        'client_message',
        withPostHogEnvironmentProperties({
            ...properties,
            message,
            level
        })
    );
});
